import { createLanguageServiceSourceFile } from 'typescript'
import { v4 as uuidv4 } from 'uuid'
import * as cst from './cst'

export type ID = string | number

export interface Node {
  id?: ID

  label?: string //Set automatically

  title?: string
  description?: string
  owner?: string
  linkedIssue?: string

  state?: string //TODO enum
  color?: string
  opacity?: number
  collapsed?: boolean
  hidden?: boolean

  shapeProperties?: any //TODO

  deps_max?: number
  deps_done?: number

  x?: number
  y?: number
  xCoord?: number
  yCoord?: number
}

export const isNode = (obj: any): obj is Node => {
  return !!obj.title//For now only title is mandatory
}

export interface Edge {
  id?: ID
  from: ID
  to: ID
}

interface MatrixElement{
  id: ID
  validity: number
}

export interface WorkspaceData {
  title: string;
  url: string;
}

class GraphData {
  private nodes: Node[]
  private edges: Edge[]

  private idCounter : number

  private names: Set<string>
  private seen: Set<ID>
  private selectedFilterOption: string
  private highlightedNodes : ID[] 
  private validityCounter : number

  private coordinatesMatrix : MatrixElement[][]
  private currentWorkspace : string
  private currentWorkspaceData : WorkspaceData

  constructor() {
    this.nodes = []
    this.edges = []
    this.idCounter = 1
    this.highlightedNodes = []
    this.names = new Set()
    this.seen = new Set()
    this.coordinatesMatrix = []
    this.validityCounter = 0
    this.currentWorkspace = localStorage.getItem("TTCurrentWorkspace") || "default"
  }

  //SAVING, LOADING
  async saveGraph() {
    const token = localStorage.getItem("TTAccessToken") 
    const url = localStorage.getItem("TTUrl")
    const graphData = JSON.stringify({
      nodes: this.nodes,
      edges: this.edges,
    })
    // localStorage.setItem("TTGraphData", serialized);
    //
    const requestOptions = {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ graphData, idCounter: this.idCounter}),
    }

    try { 
      const res = await fetch(`${url}/api/data/${this.currentWorkspace}?token=${token}`, requestOptions)
      // const data = await res.json()
      // console.log(data)
    } catch (e) {
      console.log(e)
    }
  }

  async fetchGraph() {
    const token = localStorage.getItem("TTAccessToken") 
    const url = localStorage.getItem("TTUrl")
    let data : any
    try {
      const response = await fetch(`${url}/api/data/${this.currentWorkspace}?token=${token}`)
      console.log("mindik")
      data = await response.json()
      // console.log(data)
    } catch (error) {
      console.error(error)
    }
    if (data) {
      const graphData = JSON.parse(data.graphData)
      this.nodes = graphData.nodes
      this.edges = graphData.edges

      this.currentWorkspaceData = data.workspaceData

      this.idCounter = data.idCounter
      if(this.idCounter === undefined){
        console.log("Old ID system detected. Reindexing...")
        this.reindexGraphIds()
        await this.saveGraph()
      }
    }
  }



  async importGraph(data : string){
    const oldGraphData = {
      nodes: this.nodes,
      edges: this.edges,
    }
    try {
      const graphData = JSON.parse(data)
      this.nodes = graphData.nodes
      this.edges = graphData.edges
      await this.performLayout()
    } catch (error) {
      console.log(error)
      this.nodes = oldGraphData.nodes
      this.edges = oldGraphData.edges
    }
    this.reindexGraphIds()
  }

  public exportGraph(){
    const graphData = JSON.stringify({
      nodes: this.nodes,
      edges: this.edges,
    })
    return graphData
  }
  //GETTERS, ADDERS, REMOVERS
  //
  //
  
  private getNextIdAndIncrement() : ID {
    const ret = this.idCounter
    this.idCounter ++
    return ret
  }

  private reindexGraphIds() {
    this.idCounter = 1
    const reindexMap = new Map<ID, ID>
    this.nodes.forEach(n => { //Reindex nodes
      reindexMap[n.id] = this.idCounter
      n.id = this.getNextIdAndIncrement()
    })
    this.edges.forEach( e => { //Edges
      e.from = reindexMap[e.from]
      e.to = reindexMap[e.to]
    })
  }

  public setWorkspace(workspace: string){
    this.currentWorkspace = workspace
    this.names = new Set()
  }
  public getWorkspace() : string{
    return this.currentWorkspace
  }
  public getWorkspaceData() : WorkspaceData{
    return this.currentWorkspaceData
  }

  public addNode(node: Node): ID {
    if(node.id === undefined){
      node.id = this.getNextIdAndIncrement()
    }
    node.deps_done = node.deps_max = 0
    // this.nodes.push(node)
    this.nodes = [ node, ...this.nodes ] //Start
    return node.id
  }

  public addChildNode(parent: ID, child: Node): void {
    this.addNode(child)
    const new_edge : Edge = {
      from: parent,
      to: child.id
    }
    this.addEdge(new_edge)
    this.getParentTree(child.id).forEach((parent) => {
      this.getNode(parent).deps_max = this.getSubTree(parent).length - 1
    })
  }

  public updateNode(oldNodeId : ID, node: Node): void {
    const oldNode = this.getNode(oldNodeId)
    const idx = this.nodes.indexOf(oldNode)
    if(idx === -1){
      return
    }
    this.nodes[idx] = { ...oldNode, ...node} //Override old with new
  }

  public addEdge(edge: Edge): void {
    if(edge.id === undefined){
      edge.id = uuidv4()
    }
    // this.edges.push(edge) //End
    this.edges = [ edge , ...this.edges ] //Start
    this.getParentTree(edge.from).forEach((parent) => {
      this.getNode(parent).deps_max = this.getSubTree(parent).length - 1
    })
  }

  public getNodes(): Node[] {
    return this.nodes
  }

  public getEdges(): Edge[] {
    return this.edges
  }

  public getNode(id : ID) : Node | undefined {
    return this.nodes.find((node) => node.id === id)
  }
  public getEdge(id : ID) : Edge | undefined {
    return this.edges.find((edge) => edge.id === id)
  }

  public removeNodes(nodeIds: ID[]): void {
   
    const parents = new Set<ID>()
    nodeIds.forEach((node) => {
      const parentTree = this.getParentTree(node)
        .filter(parent => !nodeIds.includes(parent))
        .forEach(parent => parents.add(parent))
    })
    this.nodes = this.nodes.filter((node) => !nodeIds.includes(node.id))
    this.edges = this.edges.filter((edge) => !nodeIds.includes(edge.to) && !nodeIds.includes(edge.from))
    parents.forEach((parent) => {
      this.getNode(parent).deps_max = this.getSubTree(parent).length - 1
    })
    parents.forEach((parent) => {
      const count = this.getSubTree(parent,true).filter((child) => {
        return this.getNode(child).state === cst.STATE_DONE
      }).length
      this.getNode(parent).deps_done = count
    })
  }
  
  // public removeNode(nodeId: ID): void {
  //   this.nodes = this.nodes.filter((node) => node.id !== nodeId)
  //   this.edges = this.edges.filter((edge) => edge.from !== nodeId && edge.to !== nodeId)
  // }

  // public removeEdge(fromNodeId: ID, toNodeId: ID): void {
  //   this.edges = this.edges.filter((edge) => edge.from !== fromNodeId || edge.to !== toNodeId)
  // }

  public removeEdge(edgeId : ID): void { //TODO 
    const edge = this.getEdge(edgeId)
    const from = edge.from
    this.edges = this.edges.filter((edge) => edge.id !== edgeId)
    this.getNode(from).deps_max = this.getSubTree(from).length - 1
    const count = this.getSubTree(from,true).filter((child) => {
      return this.getNode(child).state === cst.STATE_DONE
    }).length
    this.getNode(from).deps_done = count
  }
  public getGraph() {
    return {nodes:this.nodes, edges:this.edges}
  }  

  public getNames() {
    return this.names
  }

  public setSelectedFilterOption(newSelectedFilterOption : string){
    this.selectedFilterOption = newSelectedFilterOption
  }
  public setHighlightedNodes(newHighlightedNodes : ID[]){
    this.highlightedNodes = newHighlightedNodes
  }
  //MOVERS
  //
  //


  private changeNodeIndex(fromIndex : number, toIndex : number) {
    var element = this.nodes[fromIndex]
    this.nodes.splice(fromIndex, 1)
    this.nodes.splice(toIndex, 0, element)
  }
  private changeEdgeIndex(fromIndex : number, toIndex : number) {
    var element = this.edges[fromIndex]
    this.edges.splice(fromIndex, 1)
    this.edges.splice(toIndex, 0, element)
  }

  private swapNodes = (nodeAId: ID, nodeBId: ID) : void => {
    const nodeA = this.getNode(nodeAId)
    const nodeB = this.getNode(nodeBId)
    const indexA = this.nodes.indexOf(nodeA)
    const indexB = this.nodes.indexOf(nodeB)
    if(indexA === -1 || indexB === -1){
      return
    }
    this.nodes[indexA] = nodeB
    this.nodes[indexB] = nodeA
  }
  private swapEdges = (edgeAId: ID, edgeBId: ID) : void => {
    const edgeA = this.getEdge(edgeAId)
    const edgeB = this.getEdge(edgeBId)
    const indexA = this.edges.indexOf(edgeA)
    const indexB = this.edges.indexOf(edgeB)
    if(indexA === -1 || indexB === -1){
      return
    }
    this.edges[indexA] = edgeB
    this.edges[indexB] = edgeA
  }

  public swapNeighbor(nodeId: ID, left: boolean){
    //First, we swap the nodes themselves
    const levelOfNode = this.getLevelOfNode(nodeId)
    let neighbor : ID
    if(left){
      neighbor = this.getLeftNeighborByEdges(nodeId, levelOfNode == 0) //Swap roots if at root
    }
    else{
      neighbor = this.getRightNeighborByEdges(nodeId, levelOfNode == 0) 
    }
    if(!neighbor){
      return
    }
    this.swapNodes(nodeId, neighbor)
    //Then we find all common parents and swap the appropriate nodes
    const nodeParents = this.getAllParents(nodeId)
    const neighborParents = this.getAllParents(neighbor)
    const intersection = nodeParents.filter(element => neighborParents.includes(element))
    intersection.forEach(parent => {
      const nodeEdge = this.edges.filter(edge => edge.from == parent && edge.to == nodeId)[0].id
      const neighborEdge = this.edges.filter(edge => edge.from == parent && edge.to == neighbor)[0].id
      this.swapEdges(nodeEdge, neighborEdge)
    })
  }

  public moveNodeToEnd(nodeId : ID, start : boolean) : void{
    const node = this.getNode(nodeId)
    const parents = this.getAllParents(nodeId)
    const idxNode = this.nodes.indexOf(node)
    let idxExtreme = start ? 0 : this.nodes.length -1
    let idxEdgeExtreme = start ? 0 : this.edges.length -1
    //TODO: this is simplified but perhaps it works
    //things are moved ONLY to the end or start of the ENTIRE array

    // let parentExtreme : ID
    // console.log(`start: ${start}`)
    // parents.forEach(parent => {
    //   const children = this.getAllChildren(parent)
    //   let idx : number
    //   if(start){
    //     idx = this.nodes.indexOf(this.getNode(children[0]))
    //     if(idx > idxExtreme){ //MAX
    //       idxExtreme = idx
    //       parentExtreme = parent
    //     }
    //   }
    //   else{
    //     idx = this.nodes.indexOf(this.getNode(children[children.length -1]))
    //     if(idx < idxExtreme){ //MIN
    //       idxExtreme = idx
    //       parentExtreme = parent
    //     }
    //   }
    // })
    // const nodeExtreme = this.nodes[idxExtreme]
    // console.log(nodeExtreme)
    // //Move node and all connected edges behind the last node on all parents
    // //Node
    this.changeNodeIndex(idxNode, idxExtreme)
    // //Edges
    // const edgeExtreme = this.edges.filter(edge => edge.from == parentExtreme && edge.to == nodeExtreme)[0]
    // const idxExtremeEdge = this.edges.indexOf(edgeExtreme)
    parents.forEach(parent => {
      const edge = this.edges.filter(edge => edge.from == parent && edge.to == nodeId)[0]
      const idxEdge = this.edges.indexOf(edge)
      this.changeEdgeIndex(idxEdge, idxEdgeExtreme)
    })
  }
  //BASIC GRAPH LOGIC
  
  public getRoots(): ID[] {
    const incomingEdges = new Set<ID>()
    for (const edge of this.edges) {
      incomingEdges.add(edge.to)
    }
    const rootNodes = this.nodes.filter((node) => !incomingEdges.has(node.id))
    return rootNodes.map((node) => node.id)
  }

  public getParent(nodeId: ID): ID | undefined { //Get FIRST parent
    const incomingEdge = this.edges.find((edge) => edge.to === nodeId)
    if (incomingEdge) {
      return incomingEdge.from
    }
    return undefined
  }

  public getChild(nodeId: ID): ID | undefined { //Get FIRST child
    const outgoingEdge = this.edges.find((edge) => edge.from === nodeId)
    if (outgoingEdge) {
      return outgoingEdge.to
    }
    return undefined
  }

  public getAllParents(nodeId: ID): ID[] {
    const incomingEdges = this.edges.filter((edge) => edge.to === nodeId)
    return incomingEdges.map((edge) => edge.from)
  }

  public getAllChildren(nodeId: ID): ID[] {
    const outgoingEdges = this.edges.filter((edge) => edge.from === nodeId)
    return outgoingEdges.map((edge) => edge.to)
  }

  public getRoot(nodeId: ID): ID | undefined {
    const incomingEdges = this.edges.filter((edge) => edge.to === nodeId)
    if (incomingEdges.length === 0) {
      return nodeId
    }

    const rootId = incomingEdges[0].from
    return this.getRoot(rootId)
  }

  //DERIVED GRAPH LOGIC

  public getSubTree(nodeId: ID, excludeSelf: boolean = false): ID[] {
    const subtree: ID[] = []
    const visitedNodes = new Set<ID>()
    this.getSubTreeRecursive(nodeId, subtree, visitedNodes)
    if(excludeSelf){
      return subtree.filter(parent => parent !== nodeId)
    }
    else{
      return subtree
    }
  }

  private getSubTreeRecursive(nodeId: ID, subtree: ID[], visitedNodes: Set<ID>): void {
    const node = this.nodes.find((n) => n.id === nodeId)
    if (node && !visitedNodes.has(nodeId)) {
      subtree.push(nodeId)
      visitedNodes.add(nodeId)
      const childEdges = this.edges.filter((edge) => edge.from === nodeId)
      for (const edge of childEdges) {
        this.getSubTreeRecursive(edge.to, subtree, visitedNodes)
      }
    }
  }

  public getParentTree(nodeId: ID, excludeSelf : boolean = false): ID[] {
    const parentTree: ID[] = []
    const visitedNodes = new Set<ID>()
    this.getParentTreeRecursive(nodeId, parentTree, visitedNodes)
    if(excludeSelf){
      return parentTree.filter(parent => parent !== nodeId)
    }
    else{
      return parentTree
    }
  }

  private getParentTreeRecursive(nodeId: ID, parentTree: ID[], visitedNodes: Set<ID>): void {
    const node = this.nodes.find((n) => n.id === nodeId)
    if (node && !visitedNodes.has(nodeId)) {
      parentTree.push(nodeId)
      visitedNodes.add(nodeId)
      const parentEdges = this.edges.filter((edge) => edge.to === nodeId)
      for (const edge of parentEdges) {
        this.getParentTreeRecursive(edge.from, parentTree, visitedNodes)
      }
    }
  }

  public getRightNeighbor (node : ID) : ID | undefined { 
    let ret : ID
    ret = this.getRightNeighborFromMatrix(node)
    if(ret){
      return ret
    }
    return this.getRightNeighborByEdges(node)
  }

  public getLeftNeighbor (node : ID) : ID | undefined {
    let ret : ID
    ret = this.getLeftNeighborFromMatrix(node)
    if(ret){
      return ret
    }
    return this.getLeftNeighborByEdges(node)
  }

  public getRightNeighborFromMatrix(node : ID) : ID | undefined {
    const nodeObject : Node = this.getNode(node)
    for (let x = nodeObject.xCoord + 1; x < this.coordinatesMatrix[nodeObject.yCoord].length; x++) {
      const el = (this.coordinatesMatrix[nodeObject.yCoord][x])
      if(el && el.validity === this.validityCounter){
        return el.id
      }
    }
    return
  }

  public getLeftNeighborFromMatrix(node : ID) : ID | undefined {
    const nodeObject : Node = this.getNode(node)
    for (let x = nodeObject.xCoord - 1; x >= 0 ; x--) {
      const el = (this.coordinatesMatrix[nodeObject.yCoord][x])
      if(el && el.validity === this.validityCounter){
        return el.id
      }
    }
    return
  }

  public getRightNeighborByEdges(node : ID, crossTree = true) : ID | undefined {
    const root = this.getRoot(node)

    const levelOfNode = this.getLevelOfNode(node)
    const level = this.getLevel(root, levelOfNode, crossTree)
    const index = level.indexOf(node)
    if(index == level.length - 1 || index == -1){
        return undefined
    }
    else{
        return level[index+1]
    }
  }

  public getLeftNeighborByEdges(node : ID, crossTree = true){
    const root = this.getRoot(node)
    const levelOfNode = this.getLevelOfNode(node)
    const level = this.getLevel(root, levelOfNode, crossTree)
    const index = level.indexOf(node)
    if(index == 0 || index == -1){
        return undefined
    }
    else{
        return level[index-1]
    }
  }

  public guessCommonParent(nodeIds : ID[]) : ID {
  //Based on a selection, try to determine a common parent.
  //Used when deleting nodes.
    const parentCounts : Record<ID, number> = {}
    const updateParentCount = (parent : ID) => {
      if (parentCounts.hasOwnProperty(parent)) {
        parentCounts[parent] += 1
      } else {
        parentCounts[parent] = 1
      }
    }
    nodeIds.forEach((node) => {
      const parentTree = this.getParentTree(node)
      parentTree.forEach((parent) => {
        if(nodeIds.includes(parent)){
          return
        }
        updateParentCount(parent)
      })
    })
    const max = Math.max(...Object.values(parentCounts))
    const maxParents = Object.entries(parentCounts)
      .filter(([_, value]) => value === max) //Value equal to max...
      .map(([key, _]) => key) //And take only the key
      .sort((a,b) => {return this.getLevelOfNode(b) - this.getLevelOfNode(a)}) //Closest one
    return maxParents[0]
  }

  public getLevelOfNode(node : ID) : number{
    let current_node = node
    let level = 0
    while (true){
      const incomingEdges = this.edges.filter((edge) => edge.to === current_node)
      if (incomingEdges.length === 0) {
        break
      }
      current_node = incomingEdges[0].from
      level += 1 
    }
    return level
  }

  public getLevel(rootId: ID, level: number, crossTree : boolean): ID[] {
    const nodesOnLevel: ID[] = []
    if(crossTree){
      this.getRoots().forEach(root => {
        this.traverseLevelRecursive(root, level, nodesOnLevel)
      })
    }
    else{
      this.traverseLevelRecursive(this.getRoot(rootId), level, nodesOnLevel)
    }

    return nodesOnLevel.filter(nodeId => this.getNode(nodeId).hidden == false)
  }

  private traverseLevelRecursive(node: ID, level: number, nodesOnLevel: ID[]): void {
    if (level === 0) {
      nodesOnLevel.push(node)
    } else {
      for (const child of this.getAllChildren(node)) {
        this.traverseLevelRecursive(child, level - 1, nodesOnLevel)
      }
    }
  }

  public checkDirectedCycle(newEdge: Edge): boolean {
    const adjacentList = this.getAdjacent()
    const visited = new Set<ID>()
    const queue: ID[] = []
    const startNode = newEdge.to

    if(newEdge.to === newEdge.from){
      return true
    }

    queue.push(startNode)
    visited.add(startNode)

    while (queue.length > 0) { //BFS
      const currentNode = queue.shift()

      const adjacentNodes = adjacentList[currentNode] || []

      if (adjacentNodes.includes(newEdge.from)) {
        return true //Directed cycle found!
      }

      for (const adjacentNode of adjacentNodes) {
        if (!visited.has(adjacentNode)) {
          queue.push(adjacentNode)
          visited.add(adjacentNode)
        }
      }
    }
    return false //Not found
  }

  private getAdjacent(): Record<ID, ID[]> { //Typescript way of specifying a dictionary
    const adjacentList: Record<ID, ID[]> = {}

    for (const edge of this.edges) {
      const { from, to } = edge
      if (!adjacentList[from]) {
        adjacentList[from] = []
      }
      adjacentList[from].push(to)
    }

    return adjacentList
  }

  public getNodesByPriority(): Node[] {
    const nodes : ID[] = []
    this.getRoots().forEach(root => this.DFS(root, nodes))
    const arr = nodes.map(node=>this.getNode(node))
    return arr.filter((x, i, a) => a.indexOf(x) == i) //Make unique

  }
  private DFS(root: ID, nodes : ID[]) : void{
    this.getAllChildren(root).forEach(child => this.DFS(child,nodes))
    nodes.push(root)
  }
  //STATES
  //
  private updateParentStates(nodeId : ID) : void {
    this.getParentTree(nodeId, true).forEach((parent) => {
      const count = this.getSubTree(parent,true).filter((child) => {
        return this.getNode(child).state === cst.STATE_DONE
      }).length
      this.getNode(parent).deps_done = count
    })
  }
  public cycleStateUp(nodeId : ID) : void{
    const node = this.getNode(nodeId)
    const idx = cst.STATES.indexOf(node.state)
    let new_idx : number
    if(idx == -1){
      new_idx = cst.STATES.indexOf(cst.STATE_TODO) //Default if not assigned
    } 
    else if (idx == cst.STATES.length - 1){
      new_idx = idx
    }
    else{
      new_idx = idx + 1
    }
    node.state = cst.STATES[new_idx]
    this.updateParentStates(nodeId)
  }

  public cycleStateDown(nodeId : ID) : void{
    const node = this.getNode(nodeId)
    const idx = cst.STATES.indexOf(node.state)
    let new_idx : number
    if(idx == -1){
      new_idx = cst.STATES.indexOf(cst.STATE_TODO)
    } 
    else if (idx == 0){
      new_idx = idx
    }
    else{
      new_idx = idx - 1
    }
    node.state = cst.STATES[new_idx]
    this.updateParentStates(nodeId)
  }
  //MISC
  public toggleCollapsed(nodeId : ID) : void{
    this.getNode(nodeId).collapsed = !this.getNode(nodeId).collapsed 
  }
  public uncollapseParentTree(nodeId : ID) : boolean{
    let modified = false
    this.getParentTree(nodeId, true).forEach(id => {
      const node = this.getNode(id)
      if(node.collapsed){
        node.collapsed=false
        modified = true
      }
    })
    return modified
  }
  //MAKE IT PRETTY
  //
  private nodeColorFromState(node : Node) : void{
    let idx = cst.STATES.indexOf(node.state)
    if(idx == -1){
      node.state = cst.STATE_TODO
      idx = cst.STATES.indexOf(node.state)
    } 
    node.color = cst.COLORS[idx]
  }
  private generateNodeLabel(node : Node) : void{
    let label = ""
    label += node.title
    label += "\n"
    if(node.description){
      label += "[...]"
    }
    if(node.linkedIssue){
      if(node.description){
        label += " "
      }
      label += "🦊"
    }
    if(node.deps_max){
      label += "\n"
      if(node.deps_max == node.deps_done && node.state == cst.STATE_DONE){
        label += `_${node.deps_done}/${node.deps_max}_ *✓*`
      }
      else{
        label += `_${node.deps_done}/${node.deps_max}_`
      }
    }
    if(node.owner){
      label += "\n"
      label += node.owner
    }
    // else{
    //   // label += "\n"
    //   label += "\n"
    //   label += "No owner"
    // }
    if(node.collapsed){
      label += "\n"
      label += "*+*"
    }
    node.label = label
  }

  private dimNode(node : Node) : void{
    if(this.selectedFilterOption && this.selectedFilterOption != "None" && node.owner != this.selectedFilterOption){
      node.opacity = 0.5
    }
    else{
      node.opacity = 1
    }
  }
  
  private highlightNode (node : Node) : void{
  //TODO just one for now
    if(this.highlightedNodes && node.id == this.highlightedNodes[0]){
      node.shapeProperties = { borderDashes : true } 
    }
    else{
      node.shapeProperties = { borderDashes : false } 
    }
  }

  private decorateNode(node : Node) : void{
    this.nodeColorFromState(node)
    this.generateNodeLabel(node)
    this.dimNode(node)
    this.highlightNode(node)
  }

  public registerNodes() : void {
    this.nodes.forEach(n => this.registerNode(n))
  }

  private registerNode(node : Node) : void{
    if(node.owner){
      this.names.add(node.owner)
    }
  }

  async performLayout() {
    this.seen.clear()
    this.nodes.forEach(node => node.hidden = true)
    const roots = this.getRoots()
    let current_x = 0
    roots.forEach(root => {
      current_x = this.arrangeRecursive(root,current_x,0, false) + 2
    })
    this.assignCoordinates()
    // console.log("==============")
    // this.nodes.forEach(n=> {console.log(n.title, this.seen[n.id])})
    // console.log("==============")
  }

  private assignCoordinates() : void {
    this.validityCounter += 1
    this.nodes.forEach( node => {
      if(node.hidden){
        return
      }

      node.x = node.xCoord*150
      node.y = node.yCoord*125

      this.coordinatesMatrix[node.yCoord] = this.coordinatesMatrix[node.yCoord] || []
      this.coordinatesMatrix[node.yCoord][node.xCoord] = { id : node.id, validity : this.validityCounter }
    })
  }

  private arrangeRecursive(id : ID, x : number, y : number, inc : boolean): number {
    this.seen.add(id)
    const node = this.getNode(id)
    node.hidden = false
    this.decorateNode(node)
    if(inc){
      x+=1
    }
    node.xCoord = x
    node.yCoord = y
    if(node.collapsed){
      return x
    }
    const children = this.getAllChildren(id)
    let first = true
    children.forEach((child) => {
      if(this.seen.has(child)){
        return
      }
      x = this.arrangeRecursive(child, x, y+1, !first)
      first = false
    })
    return x
  }
}

export default GraphData

